import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dumbbell, CheckCircle, Clock, Flame, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function WeeklyChallengeCard({ childId }) {
  const [challenge, setChallenge] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [showExercises, setShowExercises] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token');
        const [chalRes, histRes] = await Promise.all([
          axios.get(`${API}/challenges/current/${childId}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
          axios.get(`${API}/challenges/history/${childId}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
        ]);
        setChallenge(chalRes.data);
        setHistory(histRes.data);
      } catch { /* silent */ }
      setLoading(false);
    };
    fetch();
  }, [childId]);

  const handleComplete = async () => {
    if (!challenge?.id) return;
    setCompleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/challenges/complete/${challenge.id}`, 
        { notes: '', rating: null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Challenge completed! +30 points earned');
      setChallenge(prev => ({ ...prev, status: 'completed' }));
    } catch {
      toast.error('Failed to complete challenge');
    }
    setCompleting(false);
  };

  if (loading) return <div className="animate-pulse h-40 bg-zinc-900/50 rounded-xl" />;
  if (!challenge) return null;

  const isCompleted = challenge.status === 'completed';
  const streak = history?.stats?.current_streak || 0;
  const difficultyColour = { easy: 'text-green-400 bg-green-500/20', moderate: 'text-amber-400 bg-amber-500/20', hard: 'text-red-400 bg-red-500/20' };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="weekly-challenge">
      {/* Header */}
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isCompleted ? 'bg-green-500/20' : 'bg-[#02b5ff]/10'}`}>
              {isCompleted ? <CheckCircle className="h-5 w-5 text-green-400" /> : <Dumbbell className="h-5 w-5 text-[#02b5ff]" />}
            </div>
            <div>
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Weekly Challenge</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${difficultyColour[challenge.difficulty] || 'text-zinc-400 bg-zinc-800'}`}>
                  {challenge.difficulty}
                </span>
                <span className="text-[10px] text-zinc-500 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> ~{challenge.estimated_time_mins} mins
                </span>
              </div>
            </div>
          </div>
          {streak > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 bg-orange-500/10 rounded-lg">
              <Flame className="h-3.5 w-3.5 text-orange-400" />
              <span className="text-xs font-bold text-orange-400">{streak}</span>
            </div>
          )}
        </div>

        {/* Challenge Info */}
        <h4 className="text-base font-bold text-white mb-1">{challenge.title}</h4>
        <p className="text-xs text-zinc-400 mb-1">{challenge.description}</p>
        <p className="text-[10px] text-zinc-600">Focus: {challenge.focus_area}</p>
      </div>

      {/* Exercises */}
      {challenge.exercises?.length > 0 && (
        <div className="px-5 pb-2">
          <button 
            onClick={() => setShowExercises(!showExercises)}
            className="flex items-center gap-1 text-[10px] text-[#02b5ff] uppercase tracking-wider font-semibold hover:text-white transition-colors"
          >
            {showExercises ? 'Hide' : 'Show'} exercises ({challenge.exercises.length})
            <ChevronRight className={`h-3 w-3 transition-transform ${showExercises ? 'rotate-90' : ''}`} />
          </button>
          {showExercises && (
            <div className="mt-2 space-y-2">
              {challenge.exercises.map((ex, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 bg-zinc-800/40 rounded-lg">
                  <div className="w-6 h-6 rounded-full bg-[#02b5ff]/10 flex items-center justify-center text-xs font-bold text-[#02b5ff]">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white">{ex.name}</p>
                    <p className="text-[11px] text-zinc-500">{ex.sets} sets x {ex.reps}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action */}
      <div className="px-5 py-4 border-t border-zinc-800 flex items-center justify-between">
        {isCompleted ? (
          <div className="flex items-center gap-2 text-green-400">
            <CheckCircle className="h-4 w-4" />
            <span className="text-xs font-semibold">Completed this week!</span>
          </div>
        ) : (
          <Button
            onClick={handleComplete}
            disabled={completing}
            className="bg-[#02b5ff] hover:bg-[#02b5ff]/80 text-white text-xs h-8 px-4"
            data-testid="complete-challenge-btn"
          >
            {completing ? 'Saving...' : 'Mark as Completed'}
          </Button>
        )}
        <p className="text-[10px] text-zinc-600">
          {history?.stats?.completion_rate || 0}% completion rate
        </p>
      </div>
    </div>
  );
}
